import { Link } from 'react-router';
import { SEO, BreadcrumbSchema, FAQSchema, ServiceSchema, SpeakableSchema } from '@/components/seo/seo';

function RatingBadge({ rating, size = 'md' }: { rating: 'red' | 'amber' | 'green'; size?: 'sm' | 'md' | 'lg' }) {
  const colors = {
    red: 'bg-rose-500 text-white',
    amber: 'bg-amber-500 text-white',
    green: 'bg-emerald-500 text-white',
  };
  const sizes = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-3 py-1 text-sm',
    lg: 'px-4 py-2 text-base',
  };
  return (
    <span className={`inline-block font-bold rounded-full uppercase ${colors[rating]} ${sizes[size]}`}>
      {rating}
    </span>
  );
}

function QuoteCard({ quote, source }: { quote: string; source: string }) {
  return (
    <blockquote className="bg-surface-50 border-l-4 border-brand-500 p-6 rounded-r-xl">
      <p className="text-surface-800 italic">&ldquo;{quote}&rdquo;</p>
      <cite className="text-sm text-surface-600 mt-2 block not-italic">&mdash; {source}</cite>
    </blockquote>
  );
}

const faqs = [
  {
    question: 'Does Waltham Forest\'s RED DfT rating guarantee my claim will succeed?',
    answer: 'No. A RED scorecard is not automatic compensation — but it is official Department for Transport evidence that the borough\'s maintenance programme is failing against national benchmarks. Combined with the council\'s own pothole volumes, resurfacing decline and 7–10-year survey cycle on B, C and U roads, it undermines a generic Section 58 defence that the network was reasonably maintained.',
  },
  {
    question: 'The council reports 60% preventative maintenance — why did pothole repairs still more than double?',
    answer: 'Waltham Forest\'s June 2025 transparency report shows a consistent 60/40 preventative-to-reactive split since 2020/21. Yet estimated pothole fills rose from 2,755 in 2021/22 to 5,756 in 2023/24 (+109%) while carriageway resurfacing fell from 10.29 km in 2020/21 to a planned 6.155 km in 2025/26. The DfT rates condition, overall performance and best practice RED. Whatever the council counts as prevention, the published outcomes show reactive workload surging.',
  },
  {
    question: 'The DfT flags incomplete condition data for Waltham Forest — does that help my claim?',
    answer: 'It can. The Department for Transport notes that Waltham Forest\'s 2025/26 overall, condition and best-practice scorecards are based on incomplete road condition data. The council\'s own report publishes Gaist survey results for B, C and U roads from 2020 only — with wholesale condition capture on a 7–10-year cycle — while A-road percentages come from annual Vaisala surveys. On a residential U-road, the council\'s published condition evidence may not reflect your street today.',
  },
  {
    question: 'What if my pothole was on a residential or unclassified road?',
    answer: 'Unclassified roads make up 378 km — 84% of Waltham Forest\'s 451 km carriageway network. The council\'s last published U-road RED-condition figure is 18.14% from the 2020 Gaist survey, with no updated RAG breakdown since. If your incident was on a U-road, ask whether the council had current condition data, routine safety inspection records, or prior reports for that location — not just network-wide averages.',
  },
  {
    question: '2024/25 pothole repairs (4,385) were lower than 2023/24 (5,756) — is the problem fixed?',
    answer: 'Partial reduction only. 4,385 repairs remain 59% above the 2021/22 baseline of 2,755. The council projects 4,000–5,000 pothole fills for 2025/26 — treating an elevated failure rate as normal. The DfT overall rating remains RED across condition, best practice and aggregate performance.',
  },
  {
    question: 'Why does £120,000 of reactive pothole spending matter for a claim?',
    answer: 'The council states it spends at least £100,000 a year from the reactive maintenance budget on pothole repairs; in 2024/25 that spend was £120,000 against 4,385 estimated fills — roughly £27 per repair. That is the council\'s own arithmetic, not proof of negligence on its own — but it shows patching at scale while resurfacing kilometres fell 40% since 2020/21. Your claim still turns on the specific defect, prior notice and inspection records.',
  },
  {
    question: 'How do I report a pothole to Waltham Forest Council?',
    answer: 'Report potholes and surface defects via the council\'s Highways Report service at portal.walthamforest.gov.uk, or the dedicated pothole form at walthamforest.gov.uk. The council states high-risk potholes are targeted within 24 hours, lesser-risk within seven days, and others within six weeks. Prior reports strengthen a claim by demonstrating notice before your incident. Fixtyer searches for existing reports and attaches them to your claim pack.',
  },
];

export function WalthamForestCouncilPage() {
  return (
    <div className="min-h-screen">
      <SEO
        title="Waltham Forest Pothole Claims | 5,756 Repairs at 2023/24 Peak | Fixtyer"
        description="London Borough of Waltham Forest earns RED DfT scorecards on condition, overall performance and best practice. Its own report records 5,756 pothole fills in 2023/24 while carriageway resurfacing fell 40%. See what that means for your Section 58 claim."
        canonical="/councils/waltham-forest"
      />
      <BreadcrumbSchema
        items={[
          { name: 'Home', url: '/' },
          { name: 'Councils', url: '/councils' },
          { name: 'Waltham Forest', url: '/councils/waltham-forest' },
        ]}
      />
      <FAQSchema faqs={faqs} />
      <ServiceSchema
        councilName="London Borough of Waltham Forest"
        councilSlug="waltham-forest"
        rating="red"
      />
      <SpeakableSchema
        url="/councils/waltham-forest"
        headline="Waltham Forest Road Maintenance Report"
        cssSelectors={['[data-speakable="summary"]', 'h1', 'h2']}
      />

      {/* Hero */}
      <section className="relative bg-brand-950 text-white overflow-hidden">
        <div
          className="absolute top-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #f97316,
              #f97316 40px,
              #1e40af 40px,
              #1e40af 80px
            )`,
          }}
        />

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16">
          <nav className="text-sm text-brand-400 mb-8 flex items-center gap-2">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <span>/</span>
            <Link to="/councils" className="hover:text-white transition-colors">Councils</Link>
            <span>/</span>
            <span className="text-white">Waltham Forest</span>
          </nav>

          <div className="flex flex-wrap items-center gap-3 mb-4">
            <RatingBadge rating="red" size="lg" />
            <span className="text-brand-300">Overall</span>
            <span className="text-brand-500">|</span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="red" size="sm" /> <span className="text-brand-300 text-sm">Condition</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Spend</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="red" size="sm" /> <span className="text-brand-300 text-sm">Best Practice</span>
            </span>
          </div>

          <div className="bg-rose-500/10 border border-rose-500/30 rounded-xl px-4 py-3 mb-6 max-w-3xl">
            <p className="text-rose-200 text-sm">
              <strong className="text-rose-400">DfT caveat:</strong> The Department for Transport flags that
              Waltham Forest&apos;s 2025/26 overall, condition and best-practice scorecards are based on{' '}
              <strong className="text-white">incomplete road condition data</strong>.
            </p>
          </div>

          <h1 className="font-display text-4xl sm:text-5xl font-bold leading-tight mb-6">
            5,756 Pothole Repairs in 2023/24 on a Triple-RED Scorecard
          </h1>

          <p className="text-xl text-brand-200 max-w-2xl mb-8 leading-relaxed" data-speakable="summary">
            London Borough of Waltham Forest maintains <strong className="text-white">451 km of roads</strong>,
            84% unclassified residential routes. Its June 2025 transparency report records{' '}
            <strong className="text-rose-400">5,756 estimated pothole fills in 2023/24</strong> — up 109% from
            2,755 in 2021/22 — while carriageway resurfacing fell from 10.29 km to a planned 6.155 km. The DfT
            rates the borough <strong className="text-rose-400">RED</strong> on overall performance, condition and
            best practice, with spend <strong className="text-amber-400">AMBER</strong>. Section 58 still turns on
            your specific defect.
          </p>

          <div className="bg-rose-500/20 border border-rose-500/30 rounded-2xl p-6 max-w-md">
            <div className="text-6xl font-bold text-rose-400">+109%</div>
            <div className="text-white font-medium mt-2">Pothole fills at the 2023/24 peak</div>
            <div className="text-brand-300 text-sm mt-1">
              From 2,755 in 2021/22 to 5,756 in 2023/24 — while the council continued to report a 60/40
              preventative-to-reactive spend split and the DfT assigned RED scorecards.
            </div>
          </div>
        </div>

        <div
          className="absolute bottom-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #1e40af,
              #1e40af 40px,
              #f97316 40px,
              #f97316 80px
            )`,
          }}
        />
      </section>

      {/* DfT scorecard */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            What the DfT scorecards actually show
          </h2>
          <p className="text-surface-600 mb-8">
            Four separate ratings — not a single headline — from the 2025/26 local road maintenance release
          </p>

          <div className="bg-surface-50 rounded-2xl border border-surface-200 overflow-hidden mb-6">
            <table className="w-full text-sm">
              <caption className="sr-only">London Borough of Waltham Forest DfT road maintenance scorecards 2025/26</caption>
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-4 text-left font-semibold text-brand-900">Scorecard</th>
                  <th className="px-6 py-4 text-center font-semibold text-brand-900">Rating</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-surface-200">
                <tr className="bg-rose-50">
                  <td className="px-6 py-4 font-medium text-brand-900">Overall</td>
                  <td className="px-6 py-4 text-center"><RatingBadge rating="red" /></td>
                </tr>
                <tr className="bg-rose-50">
                  <td className="px-6 py-4 font-medium text-brand-900">Condition</td>
                  <td className="px-6 py-4 text-center"><RatingBadge rating="red" /></td>
                </tr>
                <tr>
                  <td className="px-6 py-4 font-medium text-brand-900">Spend</td>
                  <td className="px-6 py-4 text-center"><RatingBadge rating="amber" /></td>
                </tr>
                <tr className="bg-rose-50">
                  <td className="px-6 py-4 font-medium text-brand-900">Wider best practice</td>
                  <td className="px-6 py-4 text-center"><RatingBadge rating="red" /></td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-6">
            <div className="p-4 bg-rose-50 border border-rose-200 rounded-xl">
              <p className="text-rose-800 text-sm">
                <strong>What RED means:</strong> Waltham Forest is one of 13 authorities in England with an overall
                RED rating for 2025/26 — indicating systematic underperformance against DfT benchmarks on condition,
                spend efficiency and/or maintenance practice, not an isolated pothole.
              </p>
            </div>
            <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl">
              <p className="text-amber-800 text-sm">
                <strong>Why spend is AMBER, not GREEN:</strong> Projected 2025/26 capital spend (£686,000) matches
                the DfT allocation after four years of £0 allocations — recovery, not over-investment. Aggregate
                spend does not prove the individual defect that damaged your vehicle was inspected and repaired in time.
              </p>
            </div>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
            <p className="text-amber-800 text-sm">
              <strong>Data caveat:</strong> The DfT notes Waltham Forest&apos;s overall, condition and best-practice
              scorecards reflect incomplete road condition data. The council&apos;s transparency report confirms B, C
              and U-road wholesale surveys run on a 7–10-year cycle, with published B/C/U RAG percentages from the
              2020 Gaist survey only. Do not treat network tables as proof of your street&apos;s condition on the day
              of your incident.
            </p>
          </div>

          <p className="mt-6 text-sm text-surface-500">
            Source:{' '}
            <a
              href="https://www.gov.uk/government/publications/local-road-maintenance-ratings/local-road-maintenance-ratings-2025-to-2026"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Department for Transport — Local Road Maintenance Ratings 2025 to 2026
            </a>
          </p>
        </div>
      </section>

      {/* Network scale */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            451 km of Roads — Mostly Unclassified
          </h2>
          <p className="text-surface-600 mb-8">
            Network scale from Waltham Forest&apos;s June 2025 transparency report — where pothole claims actually happen
          </p>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <div className="bg-white border border-surface-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-brand-900">451 km</div>
              <div className="text-sm text-surface-700 font-medium mt-1">Total carriageway</div>
            </div>
            <div className="bg-rose-50 border border-rose-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-rose-600">378 km</div>
              <div className="text-sm text-rose-800 font-medium mt-1">Unclassified (U) roads</div>
              <div className="text-xs text-rose-700 mt-1">84% of the network</div>
            </div>
            <div className="bg-white border border-surface-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-brand-900">59 km</div>
              <div className="text-sm text-surface-700 font-medium mt-1">A roads</div>
              <div className="text-xs text-surface-500 mt-1">Surveyed annually (Vaisala)</div>
            </div>
            <div className="bg-white border border-surface-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-brand-900">14 km</div>
              <div className="text-sm text-surface-700 font-medium mt-1">B and C roads</div>
              <div className="text-xs text-surface-500 mt-1">7–10-year survey cycle</div>
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-6">
            <table className="w-full text-sm">
              <caption className="sr-only">Waltham Forest highway asset breakdown</caption>
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left font-semibold text-brand-900">Asset</th>
                  <th className="px-6 py-3 text-center font-semibold text-brand-900">Scale</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4">Footways</td>
                  <td className="px-6 py-4 text-center font-medium">727 km</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4">Public rights of way</td>
                  <td className="px-6 py-4 text-center font-medium">15 km (estimated)</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4">Cycleways</td>
                  <td className="px-6 py-4 text-center font-medium">38 km (estimated)</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4">Highway gullies</td>
                  <td className="px-6 py-4 text-center font-medium">~23,000 (2-year cleansing cycle)</td>
                </tr>
                <tr>
                  <td className="px-6 py-4">Structures (bridges, culverts, subways, etc.)</td>
                  <td className="px-6 py-4 text-center font-medium">100+ managed assets</td>
                </tr>
              </tbody>
            </table>
          </div>

          <QuoteCard
            quote="Wholesale condition survey data capture is carried out on a 7–10-year basis for B, C and U roads."
            source="London Borough of Waltham Forest Local Highways Maintenance Transparency Report (June 2025)"
          />
        </div>
      </section>

      {/* Condition data */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            What published condition data shows — and what it omits
          </h2>
          <p className="text-surface-600 mb-8">
            A-road surveys are annual; B, C and U-road RAG percentages in the report date from 2020
          </p>

          <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 mb-8">
            <p className="text-amber-800 text-sm">
              <strong>Survey-date caveat:</strong> Principal-road percentages come from Vaisala surveys carried out by
              Metis. B, C and U-road percentages shown below are from the Gaist survey carried out in 2020, with no
              updated RAG breakdown published for those classes in the June 2025 report. The DfT&apos;s incomplete-data
              flag reflects this gap.
            </p>
          </div>

          <div className="bg-white border border-surface-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-brand-900 mb-4">A roads (59 km) — surveyed annually</h3>
            <div className="bg-surface-50 rounded-xl overflow-hidden border border-surface-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-surface-100 border-b border-surface-200">
                    <th className="px-4 py-2 text-left font-semibold text-brand-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2022</td>
                    <td className="px-4 py-2 text-center">16.93%</td>
                    <td className="px-4 py-2 text-center">16.5%</td>
                    <td className="px-4 py-2 text-center">66.06%</td>
                  </tr>
                  <tr className="border-b border-surface-100 bg-rose-50">
                    <td className="px-4 py-2 font-mono font-bold">2023</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">25.31%</td>
                    <td className="px-4 py-2 text-center font-bold">26.62%</td>
                    <td className="px-4 py-2 text-center font-bold">47.59%</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2 font-mono">2024</td>
                    <td className="px-4 py-2 text-center">19.06%</td>
                    <td className="px-4 py-2 text-center">23.68%</td>
                    <td className="px-4 py-2 text-center">56.85%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-surface-600 text-sm">
              A-road RED condition peaked at 25.31% in 2023 before easing to 19.06% in 2024 — but A-roads are only
              13% of the carriageway network.
            </p>
          </div>

          <div className="bg-rose-50 border border-rose-200 rounded-2xl p-6 mb-8">
            <h3 className="font-semibold text-rose-900 mb-4">B, C and U roads — last published RAG data from 2020</h3>
            <div className="grid md:grid-cols-2 gap-4 mb-4">
              <div className="bg-white rounded-xl p-4 border border-rose-200">
                <div className="text-sm text-surface-600 mb-2">B and C roads (14 km) — Gaist 2020</div>
                <div className="text-sm text-rose-800">
                  Red 17.24% · Amber 50.44% · Green 32.32%
                </div>
              </div>
              <div className="bg-white rounded-xl p-4 border border-rose-200">
                <div className="text-sm text-surface-600 mb-2">U roads (378 km) — Gaist 2020</div>
                <div className="text-sm text-rose-800">
                  Red 18.14% (amber/green not separately tabulated in report)
                </div>
              </div>
            </div>
            <p className="text-rose-800 font-medium text-sm">
              Eighty-four per cent of Waltham Forest&apos;s carriageway network has no published condition update
              since 2020 — on roads surveyed only once per decade at wholesale level.
            </p>
          </div>

          <QuoteCard
            quote="We recognise that by taking an asset management-based approach to its highway asset maintenance, investment can be targeted on long term planned activities that prevent expensive short-term repairs."
            source="London Borough of Waltham Forest Local Highways Maintenance Transparency Report (June 2025)"
          />
        </div>
      </section>

      {/* Pothole repairs */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Rising pothole fill counts
          </h2>
          <p className="text-surface-600 mb-8">
            Estimated potholes filled — from the council&apos;s June 2025 transparency report
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <caption className="sr-only">Waltham Forest estimated potholes filled 2021–2026</caption>
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Potholes filled</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Change vs 2021/22</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021/22</td>
                  <td className="px-6 py-4 text-center">2,755</td>
                  <td className="px-6 py-4 text-center text-surface-500">Baseline</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2022/23</td>
                  <td className="px-6 py-4 text-center">3,315</td>
                  <td className="px-6 py-4 text-center text-amber-600">+20.3%</td>
                </tr>
                <tr className="bg-rose-50">
                  <td className="px-6 py-4 font-mono font-bold text-brand-900">2023/24</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">5,756</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">+108.9%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2024/25</td>
                  <td className="px-6 py-4 text-center">4,385</td>
                  <td className="px-6 py-4 text-center text-amber-600">+59.2%</td>
                </tr>
                <tr className="bg-brand-50">
                  <td className="px-6 py-4 font-bold text-brand-900">2025/26 (projected)</td>
                  <td className="px-6 py-4 text-center font-bold">4,000–5,000</td>
                  <td className="px-6 py-4 text-center text-surface-500">Council estimate</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <QuoteCard
              quote="This will include the repair of potholes which, on an annual basis, we spend at least £100,000 a year from the reactive maintenance budget. In 2024/25 this spend was £120,000."
              source="London Borough of Waltham Forest Local Highways Maintenance Transparency Report (June 2025)"
            />
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">£27 per fill — council arithmetic</h3>
              <p className="text-surface-600 text-sm">
                £120,000 reactive pothole spend divided by 4,385 estimated fills in 2024/25 equals roughly £27 per
                repair. That is the council&apos;s own implied unit cost — evidence of patch volume at scale, not on
                its own proof your specific defect was negligently handled.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Spending and resurfacing */}
      <section className="py-16 bg-brand-950 text-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold mb-4">
            60% prevention on paper — resurfacing down 40%
          </h2>
          <p className="text-brand-200 mb-8 max-w-2xl">
            Spending split and carriageway kilometres resurfaced — June 2025 transparency report
          </p>

          <div className="bg-brand-900/50 rounded-2xl border border-brand-800 overflow-hidden mb-8">
            <table className="w-full text-sm">
              <caption className="sr-only">Waltham Forest highway maintenance spending 2020–2026</caption>
              <thead>
                <tr className="bg-brand-800 border-b border-brand-700">
                  <th className="px-4 py-3 text-left font-semibold text-brand-300">Year</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-300">DfT capital (£)</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-300">Capital spend (£)</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-300">Revenue spend (£)</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-300">Preventative</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-300">Reactive</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-300">km resurfaced</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-brand-800">
                <tr>
                  <td className="px-4 py-3 font-medium">2025/26 (proj.)</td>
                  <td className="px-4 py-3 text-center text-emerald-400">686,000</td>
                  <td className="px-4 py-3 text-center">686,000</td>
                  <td className="px-4 py-3 text-center">4,100,000</td>
                  <td className="px-4 py-3 text-center">60%</td>
                  <td className="px-4 py-3 text-center">40%</td>
                  <td className="px-4 py-3 text-center font-bold text-rose-400">6.155*</td>
                </tr>
                <tr>
                  <td className="px-4 py-3">2024/25</td>
                  <td className="px-4 py-3 text-center text-emerald-400">211,000</td>
                  <td className="px-4 py-3 text-center">211,000</td>
                  <td className="px-4 py-3 text-center">4,100,000</td>
                  <td className="px-4 py-3 text-center">60%</td>
                  <td className="px-4 py-3 text-center">40%</td>
                  <td className="px-4 py-3 text-center">6.855</td>
                </tr>
                <tr>
                  <td className="px-4 py-3">2023/24</td>
                  <td className="px-4 py-3 text-center text-rose-400">0</td>
                  <td className="px-4 py-3 text-center">0</td>
                  <td className="px-4 py-3 text-center">4,100,000</td>
                  <td className="px-4 py-3 text-center">60%</td>
                  <td className="px-4 py-3 text-center">40%</td>
                  <td className="px-4 py-3 text-center">5.982</td>
                </tr>
                <tr>
                  <td className="px-4 py-3">2022/23</td>
                  <td className="px-4 py-3 text-center text-rose-400">0</td>
                  <td className="px-4 py-3 text-center">200,000</td>
                  <td className="px-4 py-3 text-center">4,100,000</td>
                  <td className="px-4 py-3 text-center">60%</td>
                  <td className="px-4 py-3 text-center">40%</td>
                  <td className="px-4 py-3 text-center">7.085</td>
                </tr>
                <tr>
                  <td className="px-4 py-3">2021/22</td>
                  <td className="px-4 py-3 text-center text-rose-400">0</td>
                  <td className="px-4 py-3 text-center">0</td>
                  <td className="px-4 py-3 text-center">3,900,000</td>
                  <td className="px-4 py-3 text-center">60%</td>
                  <td className="px-4 py-3 text-center">40%</td>
                  <td className="px-4 py-3 text-center">7.047</td>
                </tr>
                <tr>
                  <td className="px-4 py-3">2020/21</td>
                  <td className="px-4 py-3 text-center text-rose-400">0</td>
                  <td className="px-4 py-3 text-center">200,000</td>
                  <td className="px-4 py-3 text-center">3,750,000</td>
                  <td className="px-4 py-3 text-center">60%</td>
                  <td className="px-4 py-3 text-center">40%</td>
                  <td className="px-4 py-3 text-center">10.290</td>
                </tr>
              </tbody>
            </table>
            <p className="px-4 py-3 text-brand-400 text-xs border-t border-brand-800">
              *2025/26 carriageway figure is the council&apos;s planned minimum (6.155 km), not actual outturn.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-rose-500/20 rounded-xl p-6 border border-rose-500/30">
              <h3 className="font-semibold text-white mb-3">The 60/40 paradox</h3>
              <p className="text-brand-200 text-sm">
                Preventative share has stayed at 60% every year in the report — yet pothole fills more than doubled
                and carriageway resurfacing fell 40% from the 2020/21 baseline. The DfT&apos;s RED condition and
                best-practice scorecards align with outcomes, not the ratio alone.
              </p>
            </div>
            <div className="bg-amber-500/20 rounded-xl p-6 border border-amber-500/30">
              <h3 className="font-semibold text-white mb-3">Coverage maths</h3>
              <p className="text-brand-200 text-sm">
                At 6.155 km planned resurfacing against 451 km of carriageway, roughly 1.4% of the network would be
                treated in a single year — a full rotation would take about 73 years at that pace. The council also
                plans 2.72 km of footway resurfacing and expects 4,000–5,000 pothole fills in 2025/26.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Section 58 */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Inspections, surveys and Section 58
          </h2>
          <p className="text-surface-600 mb-8">
            How Waltham Forest says it knows network condition — and where gaps appear
          </p>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">Survey frequency</h3>
              <ul className="text-surface-600 text-sm space-y-2">
                <li>• <strong>A roads:</strong> annual Vaisala condition survey (Metis)</li>
                <li>• <strong>B, C and U roads:</strong> wholesale Gaist-style capture every 7–10 years (last published RAG: 2020)</li>
                <li>• <strong>Gullies:</strong> ~23,000 assets on a programmed 2-year cleansing cycle</li>
                <li>• <strong>Structures:</strong> inspection and maintenance within the highways regime</li>
              </ul>
            </div>
            <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">Four years of £0 DfT capital allocation</h3>
              <p className="text-surface-600 text-sm">
                From 2020/21 through 2023/24 the council received £0 DfT capital allocation in four of five years
                (with £200,000 capital spend in 2020/21 and 2022/23 from other sources). Funding resumed at £211,000
                in 2024/25 and £686,000 projected for 2025/26 — context for the resurfacing decline, not a substitute
                for defect-specific evidence.
              </p>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">Section 41 vs Section 58</h3>
            <p className="text-brand-200 mb-4">
              Under <strong className="text-white">Section 41 of the Highways Act 1980</strong>, Waltham Forest must
              maintain public highways. To defend a claim under <strong className="text-white">Section 58</strong>,
              it must show a reasonable system for inspecting and repairing the specific defect — not just publish
              a 60/40 budget split.
            </p>
            <ul className="text-brand-300 space-y-2 text-sm mb-6">
              <li>• Was your road on the 7–10-year B/C/U survey cycle — and had it been surveyed since 2020?</li>
              <li>• Did the defect meet intervention criteria during routine safety inspections?</li>
              <li>• Were there prior reports (FixMyStreet, council portal) giving actual notice?</li>
              <li>• Does photographic evidence show defect age beyond the council&apos;s stated repair timescales?</li>
            </ul>
            <p className="text-white font-medium">
              A RED overall scorecard plus surging pothole counts and stale U-road condition data give you structural
              arguments — but your claim still lives or dies on the specific pothole that damaged your vehicle.
            </p>
          </div>
        </div>
      </section>

      {/* Claiming against RED */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Claiming against a RED-rated borough
          </h2>
          <p className="text-surface-600 mb-8">
            Honest assessment: Waltham Forest is not Hillingdon — here is how the evidence breaks
          </p>

          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div className="bg-surface-50 rounded-2xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-4">What works in the council&apos;s favour</h3>
              <ul className="text-surface-700 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Spend scorecard AMBER — capital allocation and spend now aligned at £686,000 for 2025/26
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Documented asset-management policy and HIAMP framework
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> A-road green-rated share recovered to 56.85% in 2024 after the 2023 spike
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Stated repair prioritisation — 24 hours for highest-risk potholes
                </li>
              </ul>
              <p className="text-surface-500 text-sm mt-4 italic">
                Expect the council to cite policy documents and repair logs. Generic outrage will not substitute for defect-specific proof.
              </p>
            </div>

            <div className="bg-rose-50 border border-rose-200 rounded-2xl p-6">
              <h3 className="font-semibold text-rose-900 mb-4">What works in yours</h3>
              <ul className="text-rose-800 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> RED overall, condition and best-practice DfT scorecards
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> DfT incomplete-condition-data flag on those scorecards
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 378 km of U-roads — 84% of network — with 2020 condition data only
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 5,756 pothole fills in 2023/24 — 109% above 2021/22 baseline
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Carriageway resurfacing down 40% since 2020/21 despite 60% preventative split
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Four years of £0 DfT capital allocation before 2024/25 recovery
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">The winning strategy here is specificity plus official failure data</h3>
            <p className="text-brand-200 mb-4">
              Against a triple-RED borough, combine DfT scorecards and council transparency figures with{' '}
              <strong className="text-white">your specific defect</strong>:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• Prior reports of the same pothole — proof of actual notice beyond decade-old survey data</li>
              <li>• Photos showing defect size, depth and visible age (weathered edges, previous patching)</li>
              <li>• Road class — on a U-road, the 7–10-year survey gap and 2020 RAG snapshot are your strongest structural arguments</li>
              <li>• Whether the council can produce inspection records for your street, not just borough-wide averages</li>
            </ul>
            <p className="text-white font-medium">
              Fixtyer builds exactly this case: prior-report search, photo assessment, and citations from
              Waltham Forest&apos;s own June 2025 transparency data and the DfT RED scorecards where they help you.
            </p>
          </div>

          <p className="mt-8 text-sm text-surface-500">
            Source:{' '}
            <a
              href="https://www.walthamforest.gov.uk/parking-roads-and-travel/roads-and-pavements/road-and-pavement-maintenance"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              London Borough of Waltham Forest — Road and Pavement Maintenance (transparency report published June 2025)
            </a>
          </p>
        </div>
      </section>

      {/* Report a pothole CTA */}
      <section className="py-12 bg-surface-100 border-y border-surface-200">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-3">
            Report a pothole to Waltham Forest Council
          </h2>
          <p className="text-surface-600 mb-6">
            Reporting a defect creates a record the council had notice. The council requires a pothole report before
            processing a damage claim. Do this before claiming — and tell us when you reported it so we can reference
            it in your pack.
          </p>
          <a
            href="https://portal.walthamforest.gov.uk/service/Highways_Report"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center px-6 py-3 bg-brand-600 text-white font-semibold rounded-xl hover:bg-brand-700 transition-colors"
          >
            Report a highways problem — portal.walthamforest.gov.uk
            <svg className="ml-2 w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
          </a>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-brand-950 text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl font-bold mb-4">
            Hit a pothole in Waltham Forest?
          </h2>
          <p className="text-xl text-brand-200 mb-8">
            A triple-RED scorecard demands a precise claim. £35 for a professional claim pack.
          </p>

          <div className="grid sm:grid-cols-2 gap-6 mb-8">
            <div className="bg-brand-900/50 rounded-xl p-6 border border-brand-800">
              <h3 className="font-semibold text-brand-300 mb-3">DIY claim</h3>
              <ul className="text-sm text-brand-400 space-y-1 text-left">
                <li>• Submit photos and invoices</li>
                <li>• Use generic template letter</li>
                <li>• No DfT incomplete-data citation</li>
                <li>• No 7–10-year U-road survey-gap argument</li>
                <li>• No prior-report search</li>
              </ul>
            </div>
            <div className="bg-accent-500/20 rounded-xl p-6 border border-accent-500/30">
              <h3 className="font-semibold text-white mb-3">Professional claim pack</h3>
              <ul className="text-sm text-brand-200 space-y-1 text-left">
                <li>✅ Triple-RED DfT scorecards documented</li>
                <li>✅ 5,756 pothole peak and 109% rise cited</li>
                <li>✅ 40% resurfacing decline shown</li>
                <li>✅ 2020 U-road condition gap exposed</li>
                <li>✅ Prior reports searched and Section 58 rebuttal tailored</li>
              </ul>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/check?council=waltham-forest"
              className="inline-flex items-center justify-center px-8 py-4 bg-accent-500 text-white font-semibold rounded-xl hover:bg-accent-600 transition-all hover:shadow-lg hover:shadow-accent-500/25"
            >
              Order My Waltham Forest Claim Pack — £35
              <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
            <Link
              to="/road-maintenance-report"
              className="inline-flex items-center justify-center px-8 py-4 bg-white/10 text-white font-semibold rounded-xl hover:bg-white/20 transition-colors"
            >
              View all council ratings
            </Link>
          </div>

          <p className="text-brand-400 text-sm mt-6">
            No percentage fees. You keep 100% of any compensation.
          </p>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-8">
            Frequently asked questions
          </h2>

          <div className="space-y-6">
            {faqs.map((faq) => (
              <div key={faq.question} className="bg-white rounded-xl p-6 border border-surface-200">
                <h3 className="font-semibold text-brand-900 mb-2">{faq.question}</h3>
                <p className="text-surface-600 text-sm">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Attribution Footer */}
      <section className="py-8 bg-surface-100 border-t border-surface-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs text-surface-500 text-center">
            Data sources:{' '}
            <a
              href="https://www.gov.uk/government/publications/local-road-maintenance-ratings/local-road-maintenance-ratings-2025-to-2026"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Department for Transport — Local Road Maintenance Ratings 2025 to 2026
            </a>{' '}
            |{' '}
            <a
              href="https://www.walthamforest.gov.uk/parking-roads-and-travel/roads-and-pavements/road-and-pavement-maintenance"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              London Borough of Waltham Forest Local Highways Maintenance Transparency Report (June 2025)
            </a>
            . Contains public sector information licensed under the{' '}
            <a
              href="https://www.nationalarchives.gov.uk/doc/open-government-licence/version/3/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Open Government Licence v3.0
            </a>
            .
          </p>
        </div>
      </section>
    </div>
  );
}
